'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2, Plus } from 'lucide-react';
import { motion } from 'framer-motion';
import * as React from 'react';
import { FormProvider, useFieldArray, useForm, type FieldErrors } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { SetupConnectFormActions } from '@/components/setup/experience/SetupConnectFormShell';
import { SetupConnectStepSection } from '@/components/setup/experience/SetupConnectStepSection';
import { SetupProductFormCard } from '@/components/setup/experience/SetupProductFormCard';
import { SETUP_PATH_MOTION } from '@/components/setup/experience/constants';
import { useSetupCatalogSummary } from '@/components/setup/experience/hooks/useSetupCatalogSummary';
import {
  mapProductItemsToSetupProductDrafts,
  toCreateProductInput,
} from '@/components/setup/setupManualMappers';
import {
  createSetupManualProductDefaults,
  createSetupManualProductsSchema,
  isEmptySetupProductDraft,
  isSameSetupProductDraft,
  type SetupManualProductFormData,
  type SetupManualProductsFormData,
} from '@/components/setup/setupManualSchemas';
import type { SetupManualCreatedStore } from '@/components/setup/setupManualTypes';
import { Button } from '@/components/ui/button';
import { useCreateProduct, useDeleteProducts } from '@/lib/api/products';
import { parseTRPCError, type ParsedTRPCError } from '@/lib/utils/trpc-error-utils';

interface SetupManualProductExperienceStepProps {
  store: SetupManualCreatedStore;
  onProductCreated?: () => void;
  onBack: () => void;
}

/** Loads the existing catalog, then mounts the form prefilled with it. */
export function SetupManualProductExperienceStep({
  store,
  onProductCreated,
  onBack,
}: SetupManualProductExperienceStepProps) {
  const { t } = useTranslation('setup');
  const { products, isLoading } = useSetupCatalogSummary(store.id, store.baseCurrency, true);

  const storeMark = React.useMemo(
    () => ({ name: store.name, primaryColor: store.primaryColor, logoUrl: store.logoUrl }),
    [store.name, store.primaryColor, store.logoUrl],
  );

  if (isLoading && products === undefined) {
    return (
      <SetupConnectStepSection
        title={t('experience.connect.manualProductTitle')}
        description={t('experience.connect.manualProductSubtitle', { storeName: store.name })}
        storeMark={storeMark}
        storeMarkInTitle
      >
        <div className="flex items-center justify-center gap-2.5 py-16" role="status">
          <Loader2 className="text-primary size-5 animate-spin" aria-hidden />
          <span className="text-muted-foreground text-sm">
            {t('experience.connect.productForm.loadingCatalog')}
          </span>
        </div>
      </SetupConnectStepSection>
    );
  }

  return (
    <SetupManualProductsForm
      store={store}
      storeMark={storeMark}
      initialDrafts={mapProductItemsToSetupProductDrafts(products, store.baseCurrency)}
      catalogAlreadyStarted={(products?.length ?? 0) > 0}
      onProductCreated={onProductCreated}
      onBack={onBack}
    />
  );
}

interface SetupManualProductsFormProps {
  store: SetupManualCreatedStore;
  storeMark: { name: string; primaryColor: string; logoUrl?: string };
  initialDrafts: SetupManualProductsFormData['products'];
  catalogAlreadyStarted: boolean;
  onProductCreated?: () => void;
  onBack: () => void;
}

function SetupManualProductsForm({
  store,
  storeMark,
  initialDrafts,
  catalogAlreadyStarted,
  onProductCreated,
  onBack,
}: SetupManualProductsFormProps) {
  const { t } = useTranslation('setup');
  const createProductMutation = useCreateProduct();
  const deleteProductsMutation = useDeleteProducts();
  const [formError, setFormError] = React.useState<ParsedTRPCError | null>(null);
  const productListRef = React.useRef<HTMLDivElement>(null);

  const f = 'experience.connect.productForm';

  const schema = React.useMemo(
    () =>
      createSetupManualProductsSchema({
        productNameRequired: t('steps.storeIntegration.manual.productStep.validation.productName'),
        variantNameRequired: t('steps.storeIntegration.manual.productStep.validation.variantName'),
        imageRequired: t('steps.storeIntegration.manual.productStep.validation.imageRequired'),
        priceRequired: t('steps.storeIntegration.manual.productStep.validation.priceRequired'),
        priceInvalid: t('steps.storeIntegration.manual.productStep.validation.priceInvalid'),
        variantsMin: t('steps.storeIntegration.manual.productStep.validation.variantsMin'),
      }),
    [t],
  );

  const methods = useForm<SetupManualProductsFormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      products: initialDrafts.length ? initialDrafts : [createSetupManualProductDefaults()],
    },
    mode: 'onTouched',
  });
  const {
    control,
    getValues,
    setValue,
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  // Last saved version of each product, keyed by id — survives partial failures so a retry
  // skips blocks that were already persisted and never duplicates them.
  const savedDraftsRef = React.useRef<Map<string, SetupManualProductFormData> | null>(null);
  if (savedDraftsRef.current === null) {
    savedDraftsRef.current = new Map(
      initialDrafts.filter((draft) => draft.productId).map((draft) => [draft.productId as string, draft]),
    );
  }
  // Old versions of edited products + removed blocks — deleted only after all creates succeed.
  const pendingDeleteIdsRef = React.useRef<Set<string>>(new Set());

  const {
    fields: productFields,
    append: appendProduct,
    remove: removeProduct,
  } = useFieldArray({ control, name: 'products' });

  const scrollProductIntoView = React.useCallback((index: number) => {
    requestAnimationFrame(() => {
      const item = productListRef.current?.children.item(index) as HTMLElement | null;
      item?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    });
  }, []);

  const handleAddProduct = () => {
    const nextIndex = productFields.length;
    appendProduct(createSetupManualProductDefaults());
    scrollProductIntoView(nextIndex);
  };

  /** Revisit with an untouched empty form: skip validation and creation, just continue. */
  const handleFormSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    if (catalogAlreadyStarted && getValues('products').every(isEmptySetupProductDraft)) {
      event.preventDefault();
      onProductCreated?.();
      return;
    }
    void handleSubmit(onSubmit, onInvalid)(event);
  };

  /**
   * Reconcile the form with the store catalog, then advance. Writes are ordered so a failure can
   * never lose data: an edited product is recreated BEFORE its old version is deleted, and every
   * deletion (replaced or removed products) is deferred until all creates have succeeded.
   */
  const onSubmit = async (data: SetupManualProductsFormData) => {
    setFormError(null);

    try {
      const savedDrafts = savedDraftsRef.current as Map<string, SetupManualProductFormData>;
      const keptIds = new Set<string>();

      for (const [index, product] of data.products.entries()) {
        const saved = product.productId ? savedDrafts.get(product.productId) : undefined;

        if (saved && isSameSetupProductDraft(product, saved)) {
          keptIds.add(product.productId as string);
        } else {
          const created = await createProductMutation.mutateAsync(
            toCreateProductInput(store.id, product, store.baseCurrency),
          );
          if (product.productId) {
            pendingDeleteIdsRef.current.add(product.productId);
            savedDrafts.delete(product.productId);
          }
          savedDrafts.set(created.id, { ...product, productId: created.id });
          keptIds.add(created.id);
          setValue(`products.${index}.productId`, created.id, { shouldDirty: false });
        }
      }

      // Every create succeeded — now flush deletions (replaced versions + blocks removed from the form).
      for (const id of savedDrafts.keys()) {
        if (!keptIds.has(id)) pendingDeleteIdsRef.current.add(id);
      }
      if (pendingDeleteIdsRef.current.size) {
        const idsToDelete = Array.from(pendingDeleteIdsRef.current);
        await deleteProductsMutation.mutateAsync({ productIds: idsToDelete });
        for (const id of idsToDelete) {
          pendingDeleteIdsRef.current.delete(id);
          savedDrafts.delete(id);
        }
      }

      onProductCreated?.();
    } catch (error) {
      setFormError(parseTRPCError(error));
    }
  };

  const onInvalid = (formErrors: FieldErrors<SetupManualProductsFormData>) => {
    if (Array.isArray(formErrors.products)) {
      const firstInvalid = formErrors.products.findIndex(Boolean);
      if (firstInvalid >= 0) scrollProductIntoView(firstInvalid);
    }
  };

  const canRemoveProduct = productFields.length > 1;

  return (
    <FormProvider {...methods}>
      <div className="flex flex-col">
        <motion.form
          className="flex w-full max-w-none flex-col gap-0"
          onSubmit={handleFormSubmit}
          noValidate
          initial={SETUP_PATH_MOTION.actions.initial}
          animate={SETUP_PATH_MOTION.actions.animate}
          transition={{ ...SETUP_PATH_MOTION.actions.transition, delay: 0.1 }}
        >
          <SetupConnectStepSection
            title={t('experience.connect.manualProductTitle')}
            description={t('experience.connect.manualProductSubtitle', { storeName: store.name })}
            storeMark={storeMark}
            storeMarkInTitle
            footer={
              <SetupConnectFormActions
                backLabel={t('footer.back')}
                onBack={onBack}
                submitLabel={t('footer.continue')}
                isSubmitting={isSubmitting}
              />
            }
          >
            <fieldset disabled={isSubmitting} className="m-0 flex min-w-0 flex-col gap-6 border-0 p-0 pb-4">
              <div
                ref={productListRef}
                className="flex flex-col divide-y divide-[color-mix(in_srgb,var(--border)_70%,transparent)]"
              >
                {productFields.map((productField, index) => (
                  <SetupProductFormCard
                    key={productField.id}
                    index={index}
                    currency={store.baseCurrency}
                    canRemove={canRemoveProduct}
                    onRemove={removeProduct}
                  />
                ))}
              </div>

              <div className="border-t border-[color-mix(in_srgb,var(--border)_70%,transparent)] pt-5">
                <Button type="button" variant="outline" onClick={handleAddProduct} disabled={isSubmitting}>
                  <Plus className="size-4" aria-hidden />
                  {t(`${f}.addProduct`)}
                </Button>
              </div>

              {formError ? (
                <p
                  className="text-destructive m-0 rounded-md bg-[color-mix(in_srgb,var(--destructive)_8%,var(--background))] px-3 py-2.5 text-xs"
                  role="alert"
                >
                  {formError.message}
                </p>
              ) : null}
            </fieldset>
          </SetupConnectStepSection>
        </motion.form>
      </div>
    </FormProvider>
  );
}
